import type { ShaderContext, ShaderVideoFrame, ShaderPipeline, ShaderPipelineDefinition, ShaderPipelineInput, ShaderSubpassInput, ShaderTextureSource, ShaderTextureSourceOpaqueHandle, TextureFormat } from '@mimir/cutter/compositor/shaderContext';
import type { VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import type { FrameBufferWrapper, LinkedProgram, WebGlFormatEnum, WebGlTextureWrapper, WebGlWrapper } from './webGlWrapper';
import { tryGetWebGlTextureBufferFromVideoFrame, WebGlTextureBuffer } from './webGlTextureBuffer';
import { invariant } from '@mimir/common/util/invariant';
import { generateFragmentShader } from '@mimir/cutter/compositor/fragmentShader';
import { tryVideoFrameAsTexImageSource } from './texImageSource';
import type { Logger } from '@mimir/common/types/logger';
import { NO_LOGGER } from '@mimir/common/util/logging';
import { shaderVideoFrameFromVideoFrameV2 } from '@mimir/cutter/compositor/shaderVideoFrameFromVideoFrameV2';

export interface BrowserShaderPipeline extends ShaderPipeline {
  processToCanvasSync(inputs: ShaderPipelineInput[]): void;
}

export interface BrowserShaderContextOptions {
  /**
   *  If the input video frame is backed by a WebGL texture use this directly
   *  instead of reading the pixels from the texture and then creating a new
   *  texture from these pixels. Enabling this gives best performance and
   *  there is really no reason to ever have this off except for debugging.
   */
  allowDirectTextureUse: boolean,

  /**
   *  If the input video frame is backed by a TexImageSource (VideoFrame,
   *  ImageBitmap, etc) then use that directly as input when making a texture
   *  instead of reading pixels from said resource and use that pixel data
   *  to create a texture. Enabling this gives best performance and there
   *  is really no reason to ever have this off except for debugging.
   */
  allowTexImageSourceUse: boolean,
}

export class BrowserShaderContext implements ShaderContext {
  private logger: Logger = NO_LOGGER;
  constructor(
    private webGlWrapper: WebGlWrapper,
    private options: BrowserShaderContextOptions,
  ) { }

  public createPipelineSync(definition: ShaderPipelineDefinition): BrowserShaderPipeline {
    return new BrowserShaderPipelineImpl(this.logger, this.webGlWrapper, definition);
  }

  // implementation ShaderContext

  async createPipeline(definition: ShaderPipelineDefinition): Promise<BrowserShaderPipeline> {
    return this.createPipelineSync(definition);
  }

  getShaderVideoFrame(videoFrame: VideoFrameV2): ShaderVideoFrame {
    if (this.options.allowDirectTextureUse) {
      const directTexture = tryGetWebGlTextureBufferFromVideoFrame(videoFrame);
      if (directTexture !== null) {
        const browserOpaqueShaderImage : BrowserTextureSourceOpaqueHandle = {
          type: 'opaque_handle',
          browserTextureSource: {
            type: 'webgl_texture_buffer',
            webglTextureBuffer: directTexture,
          }
        };
        return {
          interface: 'rgba_linear_to_rgba_linear',
          rgbaLinearTexture: browserOpaqueShaderImage,
        };
      }
    }

    if (this.options.allowTexImageSourceUse) {
      const texImageSource = tryVideoFrameAsTexImageSource(videoFrame);
      if (texImageSource !== null) {
        const browserOpaqueShaderImage : BrowserTextureSourceOpaqueHandle = {
          type: 'opaque_handle',
          browserTextureSource: {
            type: 'tex_image_source',
            width: videoFrame.resolution.width,
            height: videoFrame.resolution.height,
            texImageSource: texImageSource,
          }
        };
        return {
          interface: 'rgba_linear_to_rgba_linear',
          rgbaLinearTexture: browserOpaqueShaderImage,
        };
      }
    }

    const result : ShaderVideoFrame | null = shaderVideoFrameFromVideoFrameV2(videoFrame);

    if (result === null) {
      throw new Error(`Unsupported pixel format: ${videoFrame.pixelFormat}`);
    }

    return result;
  }

  close(): void { }
}


// End of public API


class BrowserShaderPipelineImpl implements ShaderPipeline {
  private subpasses : PreparedSubpass[];

  constructor(
    private logger: Logger,
    private webGlWrapper: WebGlWrapper,
    definition: ShaderPipelineDefinition,
  ) {

    this.subpasses =
      definition.subpasses.map((s): PreparedSubpass => {
        const fragmentShaderSource = generateFragmentShader('webgl-2.0', s, definition.pipelineInputs);

        return {
          inputs: [...s.inputs],
          program: this.webGlWrapper.getLinkedProgram(vertexShaderSource, fragmentShaderSource.code),
          uniformNames: fragmentShaderSource.uniformNames,
          resolution: {
            width: s.outputResolution.width,
            height: s.outputResolution.height,
          },
          result: null,
        };
      });
  }

  processToCanvasSync(inputs: ShaderPipelineInput[]): void {
    this.webGlWrapper.throwIfError('Start of BrowserShaderContext::processToCanvasSync');
    const noVideoFrame = this.processInternal(inputs, true);
    invariant(noVideoFrame === null, 'Did not expect processInternal to return a video frame when rendering to canvas');
  }

  async process(inputs: ShaderPipelineInput[]): Promise<VideoFrameV2> {
    this.webGlWrapper.throwIfError('Start of BrowserShaderContext::process');
    const videoFrame = this.processInternal(inputs, false);
    invariant(videoFrame !== null, 'Expected processInternal to return a video frame when not rendering to canvas');
    return videoFrame;
  }

  processInternal(inputs: ShaderPipelineInput[], toCanvas: boolean): VideoFrameV2 | null {
    const preparedInputs : PreparedPipelineInput[] =
      prepareInputs(this.logger, this.webGlWrapper, inputs);

    const gl = this.webGlWrapper.getGlContext();
    for (let i = 0; i < this.subpasses.length; i++) {
      const subpass = this.subpasses[i];
      this.webGlWrapper.throwIfError(`Start of subpass [${i + 1} / ${this.subpasses.length}]`);
      subpass.program.use(gl);
      this.webGlWrapper.throwIfError('After program use');

      const { width, height } = subpass.resolution;

      const isLastPass = (i === this.subpasses.length - 1);

      let frameBufferWrapperOrNull : FrameBufferWrapper | null = null;
      if (!isLastPass || !toCanvas) {
        frameBufferWrapperOrNull = this.webGlWrapper.getFrameBuffer(width, height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, frameBufferWrapperOrNull.frameBuffer);
      }

      this.webGlWrapper.throwIfError('After binding frame buffer');

      let bindingIndex : number = 0;
      let textureSlot : number = 0;
      for (const input of subpass.inputs) {
        switch (input.type) {
          case 'pipeline_input': {
            const preparedInput = preparedInputs[input.pipelineInputIndex];
            switch (preparedInput.type) {
              case 'video_frame': {
                for (const texture of preparedInput.textures) {
                  texture.bind(gl, subpass.program, subpass.uniformNames[bindingIndex++], textureSlot++);
                }
                break;
              }
              case 'uniform_buffer': {
                subpass.program.updateUniformBlock(
                  gl,
                  subpass.uniformNames[bindingIndex++],
                  (data: ArrayBuffer) => {
                    (new Uint8Array(data)).set(preparedInput.data);
                  }
                );
                break;
              }
            }
            break;
          }
          case 'subpass_output': {
            const sourceSubpass = this.subpasses[input.subpassOutputIndex];
            if (sourceSubpass.result === null) {
              throw new Error('Subpass output reference used before being processed');
            }
            bindTexture(this.logger, gl, subpass.program, subpass.uniformNames[bindingIndex++], textureSlot++, sourceSubpass.result);
            break;
          }
        }
      }


      const transformLoc = subpass.program.getUniformLocation(gl, 'transform');
      const flipVertically = !toCanvas;
      gl.uniformMatrix2fv(transformLoc, false, flipVertically ? transformFlipVertically : transformIdentity);

      this.webGlWrapper.throwIfError('After setting transform matrix');

      gl.viewport(0, 0, width, height);

      this.webGlWrapper.throwIfError('After updating viewport');

      gl.drawArrays(gl.TRIANGLES, 0, 6);

      this.webGlWrapper.throwIfError('After drawing');

      if (frameBufferWrapperOrNull !== null) {
        const targetTexture = frameBufferWrapperOrNull.toTexture();
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        this.webGlWrapper.throwIfError('After unbinding frame buffer');
        subpass.result = targetTexture;
      }
    }

    const finalPass = this.subpasses[this.subpasses.length - 1];

    let outputVideoFrameOrNull : VideoFrameV2 | null = null;

    if (finalPass.result !== null) {
      outputVideoFrameOrNull = {
        resolution: finalPass.resolution,
        pixelFormat: 'rgba',
        fieldOrder: 'progressive',
        buffer: new WebGlTextureBuffer(finalPass.result),
      };

      finalPass.result = null;
    }

    for (const subpass of this.subpasses) {
      if (subpass.result !== null) {
        subpass.result.close();
        subpass.result = null;
      }
    }

    for (const preparedInput of preparedInputs) {
      if (preparedInput.type === 'video_frame') {
        for (const texture of preparedInput.textures) {
          texture.release();
        }
      }
    }

    return outputVideoFrameOrNull;
  }
  close(): void { }
}


interface PreparedTexture {
  bind(gl: WebGL2RenderingContext, linkedProgram: LinkedProgram, name: string, textureSlot: number): void;
  release(): void;
}


interface PreparedPipelineInputVideoFrame {
  type: 'video_frame',
  textures: PreparedTexture[],
}

interface PreparedPipelineInputUniformBuffer {
  type: 'uniform_buffer',
  data: Uint8Array,
}

type PreparedPipelineInput = PreparedPipelineInputVideoFrame | PreparedPipelineInputUniformBuffer;

function prepareInputs(
  logger: Logger,
  webGlWrapper: WebGlWrapper,
  inputs: ShaderPipelineInput[]
): PreparedPipelineInput[] {
  webGlWrapper.throwIfError('Before prepareInputs');

  const preparedInputs =
    inputs.map((i): PreparedPipelineInput => {
      switch (i.type) {
        case 'video_frame': return {
          type: 'video_frame',
          textures: prepareTextures(logger, webGlWrapper, i.shaderVideoFrame),
        };
        case 'uniform_block': return {
          type: 'uniform_buffer',
          data: i.data,
        };
      }
    });

  webGlWrapper.throwIfError('After prepareInputs');

  return preparedInputs;
}

interface PreparedSubpass {
  inputs: ShaderSubpassInput[],
  resolution: { width: number, height: number },
  program: LinkedProgram,
  uniformNames: string[],
  result: WebGlTextureWrapper | null,
}


function bindTexture(
  logger: Logger,
  gl: WebGL2RenderingContext,
  linkedProgram: LinkedProgram,
  name: string,
  textureSlot: number,
  wrapper: WebGlTextureWrapper
)  {
  const location = linkedProgram.getUniformLocation(gl, name);
  gl.activeTexture(gl.TEXTURE0 + textureSlot);
  gl.bindTexture(gl.TEXTURE_2D, wrapper.texture);
  if (location === null) {
    logger.warn(`No such uniform: ${name}`);
  } else {
    gl.uniform1i(location, textureSlot);
  }


}


const transformIdentity = new Float32Array([1.0, 0.0, 0.0, 1.0]);
const transformFlipVertically = new Float32Array([1.0, 0.0, 0.0, -1.0]);

const vertexShaderSource = `#version 300 es

out vec2 uv;
uniform mat2 transform;

void main() {
  vec2 positions[6] = vec2[](
    vec2(-1.0, -1.0),
    vec2(1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, -1.0),
    vec2(1.0, 1.0),
    vec2(-1.0, 1.0)
  );
  vec2 uvs[6] = vec2[](
    vec2(0.0, 1.0),
    vec2(1.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 1.0),
    vec2(1.0, 0.0),
    vec2(0.0, 0.0)
  );
  gl_Position = vec4(positions[gl_VertexID] * transform, vec2(0.0, 1.0));
  uv = uvs[gl_VertexID];
}`;

interface BrowserTextureSourceWebGlTextureBuffer {
  type: 'webgl_texture_buffer',
  webglTextureBuffer: WebGlTextureBuffer,
}

interface BrowserTextureSourceTexImageSource {
  type: 'tex_image_source',
  width: number,
  height: number,
  texImageSource: TexImageSource,
}

type BrowserTextureSource =
  BrowserTextureSourceTexImageSource |
  BrowserTextureSourceWebGlTextureBuffer;


interface BrowserTextureSourceOpaqueHandle extends ShaderTextureSourceOpaqueHandle {
  browserTextureSource: BrowserTextureSource,
}

function getBrowserTextureSource(textureSource: ShaderTextureSourceOpaqueHandle): BrowserTextureSource {
  const partial : (ShaderTextureSourceOpaqueHandle & Partial<BrowserTextureSourceOpaqueHandle>) = textureSource;
  if (partial.browserTextureSource !== undefined) {
    return partial.browserTextureSource;
  }
  throw new Error('ShaderTextureSourceOpaqueHandle is not a BrowserTextureSourceOpaqueHandle');
}

function textureFormatToWebGlFormat(textureFormat: TextureFormat): WebGlFormatEnum {
  switch (textureFormat) {
    case 'r8': return 'luminance8';
    case 'rg8': return 'rg8';
    case 'srgb8': return 'srgb8';
    case 'srgb8_alpha8': return 'srgb8_alpha8';
  }
}

function prepareTextureFromBrowserTextureSource(
  logger: Logger,
  webGlWrapper: WebGlWrapper,
  browserTextureSource: BrowserTextureSource,
): PreparedTexture {
  switch (browserTextureSource.type) {
    case 'tex_image_source': {
      const texture =
        // Although I can't really find any documentation for it, it seems that
        // the TexImageSources are in sRGB format and you only get correct
        // linear RGB values in the shader if you create them as a sRGB
        // texture. If you try to set this to linear 'rgba' you'll get a very
        // bright appearance.
        webGlWrapper.getTexture(
          'srgb8_alpha8',
          browserTextureSource.width,
          browserTextureSource.height,
          {
            sourceType: 'texImageSource',
            texImageSource: browserTextureSource.texImageSource,
          },
        );
      return {
        bind(gl: WebGL2RenderingContext, linkedProgram: LinkedProgram, name: string, textureSlot: number): void {
          bindTexture(logger, gl, linkedProgram, name, textureSlot, texture);
        },
        release(): void {
          texture.close();
        }
      };
    }
    case 'webgl_texture_buffer': {
      const textureBuffer = browserTextureSource.webglTextureBuffer.addReference();
      return {
        bind(gl: WebGL2RenderingContext, linkedProgram: LinkedProgram, name: string, textureSlot: number): void {
          bindTexture(logger, gl, linkedProgram, name, textureSlot, textureBuffer.texture);
        },
        release(): void {
          textureBuffer.close();
        }
      };
    }
  }

}

function prepareTexture(
  logger: Logger,
  webGlWrapper: WebGlWrapper,
  textureSource: ShaderTextureSource,
): PreparedTexture {
  switch (textureSource.type) {
    case 'system_memory': {
      const webGlFormat = textureFormatToWebGlFormat(textureSource.format);

      const texture =
        webGlWrapper.getTexture(
          webGlFormat,
          textureSource.width,
          textureSource.height,
          {
            sourceType: 'buffer',
            format: webGlFormat,
            arrayBuffer: textureSource.data,
          }
        );

      return {
        bind(gl: WebGL2RenderingContext, linkedProgram: LinkedProgram, name: string, textureSlot: number): void {
          bindTexture(logger, gl, linkedProgram, name, textureSlot, texture);
        },
        release(): void {
          texture.close();
        }
      };
    }
    case 'opaque_handle': {
      const browserOpaque : BrowserTextureSource = getBrowserTextureSource(textureSource);
      return prepareTextureFromBrowserTextureSource(logger, webGlWrapper, browserOpaque);
    }
  }
}

function prepareTextures(logger: Logger, webGlWrapper: WebGlWrapper, videoFrame: ShaderVideoFrame): PreparedTexture[] {
  switch (videoFrame.interface) {
    case 'bgra_linear_to_rgba_linear':
      return [
        prepareTexture(logger, webGlWrapper, videoFrame.bgraLinearTexture),
      ];
    case 'bgr0_linear_to_rgba_linear':
      return [
        prepareTexture(logger, webGlWrapper, videoFrame.bgr0LinearTexture),
      ];
    case 'rgba_linear_to_rgba_linear':
      return [
        prepareTexture(logger, webGlWrapper, videoFrame.rgbaLinearTexture),
      ];
    case 'yuv_planar_bt709_to_rgba_linear':
      return [
        prepareTexture(logger, webGlWrapper, videoFrame.luma),
        prepareTexture(logger, webGlWrapper, videoFrame.cb),
        prepareTexture(logger, webGlWrapper, videoFrame.cr),
      ];
    case 'nv12_bt709_to_rgba_linear':
      return [
        prepareTexture(logger, webGlWrapper, videoFrame.luma),
        prepareTexture(logger, webGlWrapper, videoFrame.cbcr),
      ];
  }
}

